import React from "react";
import { useNavigate } from "react-router-dom";
import useViewportWidth from "../hooks/useViewportWidth";
import phoneImage from "../assets/cellphone_bg.jpg";
import beerIcon from "../assets/page_logo.png";

function Dashboard() {
  const navigate = useNavigate();
  const viewportWidth = useViewportWidth();

  const isTablet = viewportWidth < 1200;
  const isMobile = viewportWidth < 900;

  // Phone only. A fixed 170px pill stacks one per row below 390px — two of them plus the
  // 14px gap need 354 against 350 of content box — so on a phone they take a 140px basis
  // instead and two share a row with the third centred beneath. It has to stay off desktop:
  // a flex basis makes the nav's shrink-to-fit width collapse to two pills (538px -> 348),
  // which wrapped "Become a Partner" onto a second row at every width. AC-MOBILE-F6/E7.
  const navButtonStyle = isMobile
    ? { ...topNavButtonStyle, width: "auto", flex: "1 1 140px", maxWidth: "170px" }
    : topNavButtonStyle;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        flexWrap: "nowrap",
        width: "100%",
        backgroundColor: "#F9F4EA",
        position: "relative",
      }}
    >
      <div
        style={{
          // Static on a phone: as an absolute overlay it is sized for the single row it
          // occupies on desktop, so once the pills wrap it grows down over the hero logo.
          // In the stacked column it is simply the first item. AC-MOBILE-F6.
          position: isMobile ? "static" : "absolute",
          top: "70px",
          left: 0,
          right: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "center" : "right",
          padding: isMobile ? "24px 20px 0" : isTablet ? "0 20px" : "20px 130px",
          gap: "16px",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        <div
          data-testid="home-nav"
          style={{
            display: "flex",
            gap: "14px",
            flexWrap: "wrap",
            justifyContent: isMobile ? "center" : "flex-end",
          }}
        >
          <button
            onClick={() => navigate("/contact")}
            style={{
              ...navButtonStyle,
              backgroundColor: "white",
              color: "#DE810E",
            }}
          >
            Contact Us
          </button>
          <button
            onClick={() => navigate("/admin-login")}
            style={{
              ...navButtonStyle,
              backgroundColor: "white",
              color: "#DE810E",
            }}
          >
            Partner Login
          </button>
          <button
            onClick={() => navigate("/become-a-partner")}
            style={{
              ...navButtonStyle,
              backgroundColor: "#DE810E",
              color: "white",
            }}
          >
            Become a Partner
          </button>
        </div>
      </div>

      {/* Left: Hero Content */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: isMobile ? "24px 20px 28px" : "140px 20px 40px",
          paddingLeft: isTablet ? "24px" : "144px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <img
          src={beerIcon}
          alt="page logo icon"
          style={{
            // A fixed 362px width with the 55px indent OUTSIDE it is 417px on a 390px
            // screen, ungated by isMobile — this was the home page's horizontal scroll.
            // The indent is subtracted inside the calc so the two together never exceed the
            // parent, which means the logo draws at exactly 362px wherever there is room —
            // the size it drew before the phone work — and shrinks only where there is not.
            // `box-sizing: border-box` looks like it fixes the same thing and does not: it
            // draws the logo at 362-55=307 and quietly shrinks it 15% on desktop.
            // AC-MOBILE-F1, AC-MOBILE-E7.
            //
            // On a phone the indent goes away entirely and the logo centres instead. It is
            // a deliberate left offset in a wide two-column hero; in a narrow single column
            // it is just 31px of lopsided margin with nothing on the other side.
            // AC-MOBILE-F12.
            width: isMobile ? "min(362px, 100%)" : "min(362px, calc(100% - 55px))",
            // The asset is a 400x400 square but the artwork only fills the middle 217px of
            // it, so 46% of the height the logo claimed on the page was blank. Cropping the
            // box to 362x260 rather than shrinking the logo keeps the artwork at exactly
            // the size it has always drawn at and removes 102px of dead space. `cover`
            // centres the crop, taking 51px off each end of a 362px-tall image against 74px
            // of clearance above the artwork and 91px below, so none of it is cut — and on
            // a phone, where the width clamps to 315, the crop drops to 27px against 65px
            // of clearance. AC-MOBILE-E7.
            //
            // A flat height, not `aspect-ratio`. The ratio is what this wants — it would
            // hold the crop proportional — but WebKit resolves it against a different box
            // than Chromium and Firefox for a flex-item image, and inconsistently across
            // widths: 378px tall at 1512 and 292 at 900 where the other two gave 260. A
            // pixel height needs no derivation and all three agree on it.
            height: "260px",
            objectFit: "cover",
            // Margins rather than padding for the offset, so the element box and its
            // content box are the same thing and `height` means the artwork.
            marginLeft: isMobile ? "auto" : "55px",
            marginRight: isMobile ? "auto" : undefined,
          }}
        />
        <h1
          style={{
            fontSize: "clamp(34px, 9vw, 60px)",
            margin: "0 0 20px",
            color: "#303030",
            lineHeight: 1.05,
          }}
        >
          Buy a Pal a Pint!
        </h1>
        <p
          style={{
            fontSize: "1rem",
            color: "#444",
            maxWidth: "450px",
            marginBottom: "30px",
          }}
        >
          Gift a cold one to friends at your favorite local spots — digitally,
          instantly, and with our app!
        </p>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
          <a
            href="https://play.google.com/store/apps/details?id=com.myhoom.myhoom"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              style={{ height: "40px" }}
            />
          </a>
          <a
            href="https://apps.apple.com/app/id6751858013"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              style={{ height: "40px" }}
            />
          </a>
        </div>
      </div>

      {/* Right: Phone Image with Button */}
      <div
        style={{
          flex: 1,
          position: "relative",
          minHeight: isMobile ? "460px" : "400px",
          minWidth: 0,
          height: isMobile ? "520px" : isTablet ? "700px" : "960px",
        }}
      >
        <img
          src={phoneImage}
          alt="Phone"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
        />
      </div>
    </div>
  );
}

const topNavButtonStyle = {
  border: "none",
  borderRadius: "30px",
  width: "170px",
  height: "46px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "14px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

export default Dashboard;
